<template>
  <Table
    ref="table"
    :layers-filters="layersFilters"
    @setLayerFeaturesFilters="setLayerFeaturesFilters"
    @deleteLayerFeaturesFilters="deleteLayerFeaturesFilters"
    :has-edit-buttons="true"
    :has-geometry="hasGeometry"
  >
    <template #additionalTools="{ isTasksVisible, currentLayerSelectedFeatures = [] }">
      <table-button
        v-if="isCreateTaskVisible && isTasksVisible && hasGeometry"
        v-bind="createTaskButtonData"
        @createTask="createTaskHandler(currentLayerSelectedFeatures)"
        :disabled="isButtonDisabled(currentLayerSelectedFeatures)"
        :tooltip="getCreateTaskToolitp(currentLayerSelectedFeatures)"
      />
      <main-dialog
        v-if="isTaskConfirmationDialogLoaded"
        :is-visible.sync="isTaskConfirmationDialogOpen"
        :title="$i18n.t('dialog.title.addTask')"
        :body="getDialogBodyText(currentLayerSelectedFeatures)"
        @agree="createTask"
        agree-text="dialog.agreeAdd"
      />
      <table-button
        v-if="isHydrantLayer"
        v-bind="toggleHydrantStatusVisualisationButtonData"
        @toggleHydrantStatusVisualisation="toggleHydrantStatusVisualisation"
        :tooltip="$i18n.t('button.toggleHydrantStatusVisualisation')"
      />
      <template v-if="isLightingHeadLayer">
        <table-button
          v-bind="toggleLightingHeadsHistoricalDataButton"
          @toggleLightingHeadsHistoricalData="toggleLightingHeadsHistoricalData"
          :disabled="isToggleLightingHeadsHistoricalDataDisabled(currentLayerSelectedFeatures)"
          :tooltip="getLightingHeadsHistoricalDataTooltip(currentLayerSelectedFeatures)"
        />
        <zdm-lighting-head-historical-data-modal
          v-if="lightingHeadsHistoricalDataDialogLoaded"
          :heads-ids="currentLayerSelectedFeatures || []"
          :is-visible.sync="lightingHeadsHistoricalDataDialogVisible"
        />
      </template>
      <table-button
        v-if="isLightingHeadDataSource"
        v-bind="toggleLightingHeadsGeneratePdfButton"
        @toggleLightingHeadsGeneratePdf="toggleLightingHeadsGeneratePdf"
        :disabled="isToggleLightingHeadsGeneratePdfDisabled(currentLayerSelectedFeatures)"
        :tooltip="getLightingHeadsGeneratePdfTooltip(currentLayerSelectedFeatures)"
      />
    </template>
  </Table>
</template>

<script>
import { call, get, sync } from 'vuex-pathify';

import Table from '@/components/Table.vue';
import TableButton from '@/components/TableButton';

export default {
  name: 'TableBasic',
  components: {
    Table,
    TableButton,
    MainDialog: () => import('@/components/MainDialog'),
    ZdmLightingHeadHistoricalDataModal: () => import('@/components/ZdmLightingHeadHistoricalDataModal'),
  },
  data: () => ({
    createTaskButtonData: {
      name: 'createTask',
      icon: '$createTask',
      fontSize: '20px',
      color: 'font',
    },
    toggleHydrantStatusVisualisationButtonData: {
      name: 'toggleHydrantStatusVisualisation',
      icon: 'mdi-fire-hydrant',
      fontSize: '20px',
    },
    toggleLightingHeadsHistoricalDataButton: {
      name: 'toggleLightingHeadsHistoricalData',
      icon: 'mdi-chart-box',
      fontSize: '20px',
    },
    toggleLightingHeadsGeneratePdfButton: {
      name: 'toggleLightingHeadsGeneratePdf',
      icon: 'mdi-clipboard-text',
      fontSize: '20px',
    },
    isTaskConfirmationDialogOpen: false,
    isTaskConfirmationDialogLoaded: false,
    lightingHeadsHistoricalDataDialogVisible: false,
    lightingHeadsHistoricalDataDialogLoaded: false,
  }),
  computed: {
    currentLayer: get('layers/currentLayer'),
    currentLayerId: sync('layers/currentLayer@id'),
    currentLayerDataSource: sync('layers/currentLayer@data_source_name'),
    hydrantsLayerId: get('hydrants/hydrantsLayerId'),
    layersFilters: get('layers/layersFilters'),
    lightingHeadLayerId: get('admin/modulesMapping@zdm_data.layers.head'),
    lightingHeadDataSource: get('admin/modulesMapping@zdm_data.datasources.head.datasource'),
    currentSelectedFeatures: sync('map/selectedFeatures@:currentLayerId'),
    isTasksSettingsEnabled: get('admin/settingsValues@tasks_module_enabled'),
    isTasksModuleEnabled: get('admin/additionalModules@TASKS.enabled'),
    isTasksPermissionsEnabled() {
      return this.$store.get('users/toolsPermissions')['TASKS'].main_value > 0;
    },
    isCreateTaskVisible() {
      return this.isTasksSettingsEnabled && this.isTasksModuleEnabled && this.isTasksPermissionsEnabled;
    },
    isHydrantLayer() {
      return this.currentLayerId === this.hydrantsLayerId;
    },
    isLightingHeadLayer() {
      return this.currentLayerId === this.lightingHeadLayerId;
    },
    hasGeometry() {
      return this.currentLayer.geometry_type ? true : false;
    },
    isLightingHeadDataSource() {
      return this.currentLayerDataSource === this.lightingHeadDataSource;
    },
  },
  methods: {
    getHydrantsLayerId: call('hydrants/getHydrantsLayerId'),
    isButtonDisabled(currentLayerSelectedFeatures) {
      return currentLayerSelectedFeatures.length < 1 || currentLayerSelectedFeatures.length > 300;
    },
    isToggleLightingHeadsHistoricalDataDisabled(currentLayerSelectedFeatures) {
      return currentLayerSelectedFeatures.length < 1;
    },
    isToggleLightingHeadsGeneratePdfDisabled(currentLayerSelectedFeatures) {
      return currentLayerSelectedFeatures.length < 1;
    },
    getDialogBodyText(currentLayerSelectedFeatures) {
      return this.$i18n.t('dialog.body.addTask', {
        amount: currentLayerSelectedFeatures.length,
        objects: this.$i18n.tc('dialog.body.objects', currentLayerSelectedFeatures.length),
      });
    },
    getCreateTaskToolitp(currentLayerSelectedFeatures) {
      return currentLayerSelectedFeatures.length < 1
        ? this.$i18n.t('button.toCreateTask', { suffix: this.$i18n.t('button.atLeastOneSelected') })
        : currentLayerSelectedFeatures.length > 300
        ? this.$i18n.t('button.moreThan300SelectedTask')
        : this.$i18n.t('button.addTask');
    },
    getLightingHeadsHistoricalDataTooltip(currentLayerSelectedFeatures) {
      return currentLayerSelectedFeatures.length > 0
        ? this.$i18n.t('button.toggleLightingHeadsHistoricalData')
        : this.$i18n.t('button.toToggleLightingHeadsHistoricalData', {
            suffix: this.$i18n.t('button.atLeastOneSelected'),
          });
    },
    getLightingHeadsGeneratePdfTooltip(currentLayerSelectedFeatures) {
      return currentLayerSelectedFeatures.length > 0
        ? this.$i18n.t('button.toggleLightingHeadsGeneratePdf')
        : this.$i18n.t('button.toToggleLightingHeadsGeneratePdf', {
            suffix: this.$i18n.t('button.atLeastOneSelected'),
          });
    },
    createTaskHandler(currentLayerSelectedFeatures) {
      if (currentLayerSelectedFeatures.length > 50) {
        this.openTaskConfirmationDialog();
      } else {
        this.createTask();
      }
    },
    createTask() {
      this.$root.$emit(`openTaskDialog`, {
        layerId: this.currentLayerId,
        dataSource: this.currentLayerDataSource,
        featuresIds: this.currentSelectedFeatures || [],
      });
    },
    openTaskConfirmationDialog() {
      this.isTaskConfirmationDialogOpen = true;
      this.isTaskConfirmationDialogLoaded = true;
    },
    deleteLayerFeaturesFilters(currentLayerId) {
      this.$store.set('layers/DELETE_LAYERS_FILTERS!', currentLayerId);
    },
    onLayerChanged() {
      this.$refs.table.onLayerChanged();
    },
    setLayerFeaturesFilters(filter) {
      this.$store.set('layers/SET_LAYERS_FILTERS!', filter);
    },
    toggleHydrantStatusVisualisation() {
      this.$root.$emit('toggleHydrantStatusVisualisation');
    },
    toggleLightingHeadsHistoricalData() {
      this.lightingHeadsHistoricalDataDialogLoaded = true;
      this.lightingHeadsHistoricalDataDialogVisible = true;
    },
    toggleLightingHeadsGeneratePdf() {
      this.$router.push({
        name: 'modernization',
        params: { lid: this.currentLayerId },
      });
      this.$root.$emit('toogleOffSelection');
    },
    async init() {
      await this.getHydrantsLayerId();
    },
  },
  mounted() {
    this.init();
  },
};
</script>
